import { useEffect, useRef } from "react";
import type { Map as MapLibre, Marker } from "maplibre-gl";

// A map like the app's (ui/map/VoxieMap.kt): MapLibre with OpenFreeMap's "liberty" style; MapLibre
// is loaded only when a map is shown. Attribution stays on (the app shows "© OpenStreetMap").

export const STYLE = "https://tiles.openfreemap.org/styles/liberty";

export interface Pin {
  id: string;
  lat: number;
  lon: number;
  color: string;
  label?: string;
  accuracy?: number;
}

export function MapView({ pins, center, zoom = 14, interactive = true, onPick, fit = false, className }: { pins: Pin[]; center?: [number, number]; zoom?: number; interactive?: boolean; onPick?: (lat: number, lon: number) => void; fit?: boolean; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibre | null>(null);
  const markers = useRef(new Map<string, Marker>());
  const lib = useRef<typeof import("maplibre-gl") | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;

  useEffect(() => {
    let alive = true;
    void (async () => {
      const ml = await import("maplibre-gl");
      await import("maplibre-gl/dist/maplibre-gl.css");
      if (!alive || !box.current) return;
      lib.current = ml;
      const first = pins[0];
      const m = new ml.Map({
        container: box.current,
        style: STYLE,
        center: center ? [center[1], center[0]] : first ? [first.lon, first.lat] : [16.37, 48.21],
        zoom,
        interactive,
        attributionControl: { compact: true },
      });
      if (interactive) m.addControl(new ml.NavigationControl({ showCompass: false }), "top-right");
      m.on("click", (e) => pick.current?.(e.lngLat.lat, e.lngLat.lng));
      map.current = m;
      sync();
    })();
    return () => {
      alive = false;
      map.current?.remove();
      map.current = null;
      markers.current.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(sync);

  function sync() {
    const m = map.current;
    const ml = lib.current;
    if (!m || !ml) return;
    const seen = new Set<string>();
    for (const p of pins) {
      seen.add(p.id);
      let marker = markers.current.get(p.id);
      if (!marker) {
        const el = document.createElement("div");
        el.style.cssText = `width:18px;height:18px;border-radius:50%;background:${p.color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)`;
        if (p.label) el.title = p.label;
        marker = new ml.Marker({ element: el }).setLngLat([p.lon, p.lat]).addTo(m);
        if (p.label) marker.setPopup(new ml.Popup({ offset: 14, closeButton: false }).setText(p.label));
        markers.current.set(p.id, marker);
      } else {
        marker.setLngLat([p.lon, p.lat]);
      }
    }
    for (const [id, marker] of markers.current) if (!seen.has(id)) (marker.remove(), markers.current.delete(id));
    if (fit && pins.length > 1) {
      const lons = pins.map((p) => p.lon);
      const lats = pins.map((p) => p.lat);
      m.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], { padding: 60, maxZoom: 16, duration: 0 });
    }
  }

  return <div ref={box} className={className} style={{ width: "100%", height: "100%" }} />;
}
